import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SaveIcon from '@mui/icons-material/Save';
import { useNotify, useRecordContext, useRefresh, type RaRecord } from 'react-admin';
import { SectionCard } from '../shared/Panel';
import {
  fetchWriteDetails,
  fetchWriteDetailsProposal,
  saveWriteDetails,
  toErrorMessage,
  typeNeedsForklift,
  type WriteDetails as Details,
  type WriteStation,
  type OrderingParty,
  type WriteHeader,
  type ForkliftDetails,
} from './viaWriteDetailsApi';
import { recordScoped } from '../shared/recordScoped';
import { differs, useDiscardGuard } from '../shared/DiscardChangesDialog';
import { fieldLabel, questionAbout, requiredInputAttrs, useRequiredFields } from '../shared/requiredFields';
import { useIntakeFlow } from '../admin/intake/IntakeFlowCard';

/**
 * Uzupełnianie szczegółów wymaganych przez importer VIA (W10).
 *
 * ## Po co ten formularz
 *
 * Nagłówek zlecenia pokrywa 6 z około 25 pól, których wymaga importer.
 * Resztę — pełny adres zleceniodawcy, adresy i **oba** okna czasowe każdej
 * stacji, nazwy i liczby jednostek transportowych, objętość — miała
 * dostarczyć ekstrakcja z maila. Dopóki jej nie ma, wpisuje je człowiek,
 * bo bez nich plik albo nie utworzy zlecenia w VIA, albo utworzy je błędnie.
 *
 * ## „Wypełnij z danych, które mamy"
 *
 * Propozycja przepisuje **wyłącznie wartości, które gdzieś istnieją**:
 * kształt z prezlecenia, miasta i daty z nagłówka, kod i kraj z dopasowanego
 * zlecenia VIA. Czego nie mamy, zostaje puste — i to jest celowe. Wpisana
 * godzina „08:00" albo wyliczona z wagi objętość byłyby zmyśleniem, którego
 * w VIA nikt już nie odróżni od ustalenia z klientem.
 *
 * ## Zapis unieważnia akceptację
 *
 * Operator zatwierdza konkretną treść. Zmiana danych po zatwierdzeniu cofa
 * zlecenie do szkicu — i mówimy o tym wprost, zamiast zostawić planistę
 * z wyszarzonym przyciskiem przy następnej próbie zapisu pliku.
 */

const EMPTY_STATION: WriteStation = {
  id: null,
  companyName: null,
  address: null,
  zip: null,
  city: null,
  countryId: null,
  fromDate: null,
  fromTime: null,
  untilDate: null,
  untilTime: null,
  transportUnitName: null,
  transportUnitCount: null,
};

/** Pole zleceniodawcy: klucz w danych i etykieta dla człowieka. */
export const ORDERING_PARTY_FIELDS: Array<[keyof OrderingParty, string]> = [
  ['nazwa', 'Nazwa'],
  ['idZewnetrzny', 'Id w systemie klienta'],
  ['adres', 'Ulica i numer'],
  ['kod', 'Kod pocztowy'],
  ['miasto', 'Miasto'],
  ['kraj', 'Kraj (2 litery)'],
];

export const STATION_FIELDS: Array<[keyof WriteStation, string, string?]> = [
  ['id', 'Id punktu'],
  ['companyName', 'Firma'],
  ['address', 'Ulica i numer'],
  ['zip', 'Kod'],
  ['city', 'Miasto'],
  ['countryId', 'Kraj'],
  ['fromDate', 'Okno od (dzień)', 'date'],
  ['fromTime', 'Okno od (godz.)', 'time'],
  ['untilDate', 'Okno do (dzień)', 'date'],
  ['untilTime', 'Okno do (godz.)', 'time'],
  ['transportUnitName', 'Jednostka (np. EUR)'],
  ['transportUnitCount', 'Liczba jednostek', 'number'],
];

/**
 * Kolumny z szerokości karty, nie ekranu. Panel stoi w połowie ekranu,
 * a siatka czterech kolumn od `md` przy 1366 px ucinała daty („01.10.202”)
 * i etykiety („Jednostka (np. E…”, 28.09.2026). Pole ma co najmniej 200 px —
 * tyle potrzebuje data z ikoną kalendarza.
 */
const FIELD_GRID = 'repeat(auto-fill, minmax(min(100%, 200px), 1fr))';

const asText = (v: unknown) => (v === null || v === undefined ? '' : String(v));

const EMPTY_HEADER: WriteHeader = { customerOrderNumber: null, requiredVehicleType: null };

const WriteDetailsPanel = () => {
  const record = useRecordContext<RaRecord>();
  const notify = useNotify();
  const refresh = useRefresh();

  const [details, setDetails] = useState<Details | null>(null);
  // Dane w wersji z serwera (po otwarciu i po zapisie) — od nich liczymy,
  // czy „Przeładuj” skasowałby coś, czego nikt nie zapisał.
  const [saved, setSaved] = useState<Details | null>(null);
  // Numer zamówienia klienta i wymagany typ pojazdu (16.10.2026) — pola
  // zlecenia, nie `structure`; zapisują się razem ze szczegółami.
  const [header, setHeader] = useState<WriteHeader>(EMPTY_HEADER);
  const [savedHeader, setSavedHeader] = useState<WriteHeader>(EMPTY_HEADER);
  const [canEdit, setCanEdit] = useState(true);
  const [blockReason, setBlockReason] = useState<string | null>(null);
  const [sources, setSources] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const { guard, dialog } = useDiscardGuard(
    details !== null && (differs(details, saved) || differs(header, savedHeader)),
  );
  // Pola obowiązkowe półgrubą czcionką (24.10.2026, `requiredFieldsHighlight`)
  // i pytanie Administracji przy polu (`intakeQuestionField`) — obieg
  // prezlecenia, z którego jest ta koperta.
  const isRequired = useRequiredFields();
  const { flow } = useIntakeFlow((record?.preOrderId as number | null | undefined) ?? undefined);
  const question = flow?.openQuestion ?? null;
  const label = (text: string, key: string) => fieldLabel(text, isRequired(key), { forInput: true });
  const hint = (key: string, fallback?: string) =>
    questionAbout(question, [key]) ? `Pytanie Administracji: ${question?.message ?? '—'}` : fallback;
  const hintSlot = (key: string) => ({
    htmlInput: requiredInputAttrs(isRequired(key)),
    ...(questionAbout(question, [key])
      ? { formHelperText: { sx: { color: 'warning.main' }, 'data-testid': `intake-question-marker-${key}` } as object }
      : {}),
  });

  if (!record) return null;

  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await action();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const openDetails = () =>
    void run(async () => {
      const w = await fetchWriteDetails(record.id);
      setDetails(w.details);
      setSaved(w.details);
      setHeader(w.header ?? EMPTY_HEADER);
      setSavedHeader(w.header ?? EMPTY_HEADER);
      setCanEdit(w.canEdit);
      setBlockReason(w.editBlockReason);
      setSources([]);
    });

  const fillFromKnownData = () =>
    void run(async () => {
      const p = await fetchWriteDetailsProposal(record.id);
      setDetails(p.details);
      setSources(p.sources);
      notify('Propozycja wstawiona — sprawdź i zapisz', { type: 'info' });
    });

  const save = () =>
    void run(async () => {
      const w = await saveWriteDetails(record.id, details ?? {}, header);
      setDetails(w.details);
      setSaved(w.details);
      setHeader(w.header ?? header);
      setSavedHeader(w.header ?? header);
      setSources([]);
      notify(
        w.approvalRevoked
          ? 'Zapisano. Zlecenie wróciło do szkicu — dane zmieniły się po zatwierdzeniu, więc akceptacja wymaga powtórzenia.' +
              (w.fileOutdated
                ? ' Plik w skrzynce ma poprzednią treść — po zatwierdzeniu wytwórz go ponownie.'
                : '')
          : 'Szczegóły zapisane',
        { type: w.approvalRevoked ? 'warning' : 'success' },
      );
      refresh();
    });

  const setHeaderField = (field: keyof WriteHeader, v: string) =>
    setHeader((h) => ({ ...h, [field]: v }));

  const setForkliftField = (field: keyof ForkliftDetails, v: string) =>
    setDetails((d) => ({
      ...(d ?? {}),
      forklift: { ...(d?.forklift ?? {}), [field]: v as unknown as number },
    }));

  const setOrderingPartyField = (field: keyof OrderingParty, v: string) =>
    setDetails((d) => ({
      ...(d ?? {}),
      zleceniodawca: { ...(d?.zleceniodawca ?? {}), [field]: v },
    }));

  const setStationField = (i: number, field: keyof WriteStation, v: string) =>
    setDetails((d) => {
      const stations = [...(d?.stacje ?? [])];
      stations[i] = { ...stations[i], [field]: v };
      return { ...(d ?? {}), stacje: stations };
    });

  const addStation = () =>
    setDetails((d) => ({
      ...(d ?? {}),
      stacje: [...(d?.stacje ?? []), { ...EMPTY_STATION }],
    }));

  const removeStation = (i: number) =>
    setDetails((d) => ({
      ...(d ?? {}),
      stacje: (d?.stacje ?? []).filter((_, j) => j !== i),
    }));

  const locked = busy || !canEdit;
  const stations = details?.stacje ?? [];
  // Sekcja czynności wózkowej według typu w formularzu — pojawia się od razu
  // po wpisaniu „+FORKLIFT”, nie dopiero po zapisie.
  const forkliftRequired = typeNeedsForklift(header.requiredVehicleType);

  return (
    <SectionCard title="Dane dla importera VIA">
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: 1 }}
      >
        Nagłówek zlecenia pokrywa 6 z około 25 pól, których wymaga importer.
        Puste pole zostaje puste — nie wpisujemy wartości „na wszelki wypadek",
        bo w VIA nikt już nie odróżni jej od ustalenia z klientem.
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }} useFlexGap>
        <Button
          size="small"
          variant="outlined"
          disabled={busy}
          // Przeładowanie zastępuje formularz danymi z serwera — przy
          // niezapisanych zmianach najpierw pytamy.
          onClick={() => guard(openDetails)}
        >
          {details === null ? 'Otwórz dane' : 'Przeładuj'}
        </Button>
        <Button
          size="small"
          startIcon={<AutoFixHighIcon />}
          disabled={locked || details === null}
          onClick={fillFromKnownData}
        >
          Wypełnij z danych, które mamy
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={locked || details === null}
          onClick={save}
        >
          Zapisz
        </Button>
        {busy && <CircularProgress size={18} sx={{ alignSelf: 'center' }} />}
      </Stack>

      {details === null && (
        <Typography variant="body2" color="text.secondary">
          Kliknij „Otwórz dane", żeby uzupełnić pola wymagane przez importer.
        </Typography>
      )}

      {blockReason && (
        <Alert severity="info" sx={{ mb: 1 }}>
          {blockReason}
        </Alert>
      )}

      {sources.length > 0 && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Wstawiono propozycję — <strong>jeszcze nie zapisaną</strong>. Źródła:{' '}
          {sources.join('; ')}. Pola, których nie mamy, zostały puste celowo.
        </Alert>
      )}

      {details !== null && (
        <>
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            Zlecenie
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gap: 1,
              gridTemplateColumns: FIELD_GRID,
              mb: 1,
            }}
          >
            <TextField
              size="small"
              label={label('Nr zamówienia klienta', 'customerOrderNumber')}
              value={asText(header.customerOrderNumber)}
              disabled={locked}
              onChange={(e) => setHeaderField('customerOrderNumber', e.target.value)}
              helperText={hint(
                'customerOrderNumber',
                'VIA nada swój numer, a po tym rozpozna zlecenie import z TMS. Bez niego pliku nie wytworzymy.',
              )}
              slotProps={hintSlot('customerOrderNumber')}
            />
            <TextField
              size="small"
              label="Wymagany typ pojazdu"
              value={asText(header.requiredVehicleType)}
              disabled={locked}
              onChange={(e) => setHeaderField('requiredVehicleType', e.target.value)}
              helperText="Jak w TMS, np. JUMBO TRUCK. Z wózkiem: JUMBO TRUCK+FORKLIFT."
            />
          </Box>

          {forkliftRequired && (
            <Box
              data-testid="forklift-details"
              sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1, mb: 1 }}
            >
              <Typography variant="subtitle2">Czynność wózkowa — drugi plik</Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 1 }}
              >
                Do VIA pójdzie osobne zlecenie z typem FORKLIFT, w ostatnim punkcie
                rozładunku, z numerem klienta i dopiskiem „-liczbaXPALF”. Cenę i liczbę
                czynności wpisujesz sam — nie wyliczamy ich z ceny transportu.
              </Typography>
              <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: FIELD_GRID }}>
                <TextField
                  size="small"
                  type="number"
                  label="Liczba czynności"
                  value={asText(details.forklift?.count)}
                  disabled={locked}
                  onChange={(e) => setForkliftField('count', e.target.value)}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Cena czynności"
                  value={asText(details.forklift?.price)}
                  disabled={locked}
                  onChange={(e) => setForkliftField('price', e.target.value)}
                  helperText="W walucie zlecenia"
                />
              </Box>
            </Box>
          )}

          <Divider sx={{ my: 1 }} />

          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            Zleceniodawca
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gap: 1,
              gridTemplateColumns: FIELD_GRID,
              mb: 1,
            }}
          >
            {ORDERING_PARTY_FIELDS.map(([field, fieldName]) => (
              <TextField
                key={field}
                size="small"
                label={label(fieldName, `orderingParty.${field}`)}
                value={asText(details.zleceniodawca?.[field])}
                disabled={locked}
                onChange={(e) => setOrderingPartyField(field, e.target.value)}
                helperText={hint(`orderingParty.${field}`)}
                slotProps={hintSlot(`orderingParty.${field}`)}
              />
            ))}
            <TextField
              size="small"
              type="number"
              label={label('Objętość przesyłki', 'volume')}
              value={asText(details.objetosc)}
              disabled={locked}
              onChange={(e) =>
                setDetails((d) => ({
                  ...(d ?? {}),
                  objetosc: e.target.value as unknown as number,
                }))
              }
              helperText={hint('volume', 'Nie wyliczamy jej z wagi')}
              slotProps={hintSlot('volume')}
            />
          </Box>

          <Divider sx={{ my: 1 }} />

          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', mb: 1 }}
          >
            <Typography variant="subtitle2">Punkty ({stations.length})</Typography>
            <Chip size="small" variant="outlined" label="pierwsza = załadunek" />
            <Button
              size="small"
              startIcon={<AddIcon />}
              disabled={locked}
              onClick={addStation}
            >
              Dodaj punkt
            </Button>
          </Stack>

          {stations.length === 0 && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              Bez ani jednego punktu importer nie utworzy zlecenia.
            </Alert>
          )}

          {stations.map((s, i) => (
            <Box
              key={i}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 1,
                mb: 1,
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: 'center', mb: 1 }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Punkt {i + 1}
                  {i === 0 ? ' (załadunek)' : ''}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Tooltip title="Usuń punkt">
                  <IconButton
                    size="small"
                    disabled={locked}
                    onClick={() => removeStation(i)}
                    aria-label={`Usuń punkt ${i + 1}`}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Box
                sx={{
                  display: 'grid',
                  gap: 1,
                  gridTemplateColumns: FIELD_GRID,
                }}
              >
                {STATION_FIELDS.map(([field, fieldName, inputType]) => (
                  <TextField
                    key={String(field)}
                    size="small"
                    label={label(fieldName, `station.${String(field)}`)}
                    helperText={hint(`station.${String(field)}`)}
                    type={inputType ?? 'text'}
                    value={asText(s[field])}
                    disabled={locked}
                    // Pole daty i godziny musi mieć etykietę zwiniętą od
                    // początku — inaczej nakłada się na wpisaną wartość.
                    slotProps={{
                      ...hintSlot(`station.${String(field)}`),
                      ...(inputType === 'date' || inputType === 'time' ? { inputLabel: { shrink: true } } : {}),
                    }}
                    onChange={(e) => setStationField(i, field, e.target.value)}
                  />
                ))}
              </Box>
            </Box>
          ))}

          <Typography variant="caption" color="text.secondary">
            Importer wymaga <strong>obu</strong> krańców okna czasowego w każdym
            punkcie oraz nazwy i liczby jednostek — bez nich ilość importuje się
            jako zero. Czego brakuje po zapisie, pokaże podgląd pliku.
          </Typography>
        </>
      )}
      {dialog}
    </SectionCard>
  );
};

/** Stan panelu należy do rekordu — przejście na inny rekord zaczyna od zera (`recordScoped`). */
export const WriteDetails = recordScoped(WriteDetailsPanel);
